import { EventMarker, EventWithRels } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { atomWithDebounce } from '@/atoms/debounce';
import { filtersAtom } from '@/atoms/filters/filter';
import { pageAndOrderParamsAtom } from '@/atoms/general';
import { comparisonIdAtom } from '@/atoms/queryParams';
import { EVENT_MARKERS_LIMIT } from '@/consts/visualisations';
import { playerMetricEventsUrl } from '@/query/url';
import { Nullable } from '@/types/generic';
import { NestedObject } from '@/types/object';
import { convertFilterParamsToString, hasEmptyInFilters } from '@/utils/api';
import { convertMarkerDataIntoMarkers, processEventDataAtom } from '@/utils/atoms/eventData';
import { fetchClientAtom } from '../client';

export const playerComparisonEventFilterDebounceObject = atomWithDebounce<Nullable<NestedObject>>(null);
const { debouncedValueAtom: playerComparisonEventFilterAtom } = playerComparisonEventFilterDebounceObject;

const rawPlayerComparisonEventMarkersAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const metricKey = get(filtersAtom).metric;
  const comparisonId = get(comparisonIdAtom);
  const filterParams = get(playerComparisonEventFilterAtom);
  const queryKey = ['playerComparisonEventMarkersForMetric', metricKey, comparisonId, filterParams] as const;

  const queryFn = async ({ queryKey: [, metricKey, comparisonId, filterParams] }: { queryKey: typeof queryKey }) => {
    if (!comparisonId || !metricKey || hasEmptyInFilters(filterParams)) return [];
    return (await fetch(
      `/player/${comparisonId}/metric/${metricKey}/event-markers${convertFilterParamsToString({
        ...filterParams,
        limit: EVENT_MARKERS_LIMIT,
      })}`,
    )) as Promise<EventMarker[]>;
  };

  return { queryKey, queryFn };
});

export const playerComparisonEventMarkersForMetricAtom = atom(async get => {
  const data = await get(rawPlayerComparisonEventMarkersAtom);
  return convertMarkerDataIntoMarkers(data.data);
});

const rawPlayerComparisonEventsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const metricKey = get(filtersAtom).metric;
  const comparisonId = get(comparisonIdAtom);
  const filterParams = get(playerComparisonEventFilterAtom);
  const pageAndOrderParams = get(pageAndOrderParamsAtom);
  const queryKey = [
    'playerComparisonEventsForMetric',
    metricKey,
    comparisonId,
    filterParams,
    pageAndOrderParams,
  ] as const;

  const queryFn = async ({
    queryKey: [, metricKey, comparisonId, filterParams, pageAndOrderParams],
  }: {
    queryKey: typeof queryKey;
  }) => {
    if (!comparisonId || !metricKey || hasEmptyInFilters(filterParams)) return [];
    const url = playerMetricEventsUrl(comparisonId, metricKey, {
      ...pageAndOrderParams,
      ...filterParams,
    });
    if (!url) return [];
    return (await fetch(url)) as Promise<EventWithRels[]>;
  };

  return { queryKey, queryFn };
});

export const playerComparisonEventsForMetricAtom = atom(async get =>
  processEventDataAtom(await get(rawPlayerComparisonEventsAtom)),
);

const rawPlayerComparisonEventsCountAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const metricKey = get(filtersAtom).metric;
  const comparisonId = get(comparisonIdAtom);
  const filterParams = get(playerComparisonEventFilterAtom);
  const queryKey = ['playerComparisonEventsForMetricCount', metricKey, comparisonId, filterParams] as const;

  const queryFn = async ({ queryKey: [, metricKey, comparisonId, filterParams] }: { queryKey: typeof queryKey }) => {
    if (!comparisonId || !metricKey || hasEmptyInFilters(filterParams)) return 0;
    return (await fetch(
      `/player/${comparisonId}/metric/${metricKey}/events/count${convertFilterParamsToString(filterParams)}`,
    )) as Promise<number>;
  };

  return { queryKey, queryFn };
});

export const playerComparisonEventsForMetricCountAtom = atom(
  async get => (await get(rawPlayerComparisonEventsCountAtom)).data,
);
